/**
 * Single dispatcher for decorative-menu:* events. Routes by target.targetKind
 * to the explicit-target wrappers in image-editor.js. Used by both the direct
 * decorative right-click menu and the section right-click menu.
 *
 * Selection is explicit; Replace uses the same dispatcher as every other
 * surface so right-click cannot drift from the panel and command bar.
 */

import { eventBus } from '../core/events.js';
import { reportError } from '../core/report-error.js';
import { bridge } from '../editing/bridge.js';
import {
  removeDecorativeForTarget,
  removeBackgroundImageForTarget
} from '../editing/image-editor.js';
import { openReplaceDialog } from '../editing/replace-dispatcher.js';

let _initialized = false;

export function initDecorativeActionHandlers() {
  if (_initialized) return;
  _initialized = true;
  eventBus.on('decorative-menu:select', onSelect);
  eventBus.on('decorative-menu:replace', onReplace);
  eventBus.on('decorative-menu:remove', onRemove);
}

export function destroyDecorativeActionHandlers() {
  if (!_initialized) return;
  eventBus.off('decorative-menu:select', onSelect);
  eventBus.off('decorative-menu:replace', onReplace);
  eventBus.off('decorative-menu:remove', onRemove);
  _initialized = false;
}

function onReplace(payload) {
  const target = payload && payload.target;
  if (!target) return;
  if (!['decorative-divider', 'decorative-background'].includes(target.targetKind)) {
    reportError(
      'decorative-action',
      new Error(`Unknown target kind: ${target.targetKind || 'missing'}`),
      { user: 'toast' }
    );
    return;
  }
  void openReplaceDialog({ target, context: { triggerEl: getTriggerEl(payload) } });
}

function onSelect(payload) {
  const target = payload?.target;
  if (!target?.instanceId || !target?.targetRef) return;
  eventBus.emit('iframe:element-clicked', target);
  if (target.rect) eventBus.emit('iframe:element-rect', target);
  if (target.targetRef.domain === 'dom') {
    bridge.send('reselect-editable', {
      instanceId: target.instanceId,
      eid: target.targetRef.eid,
      editableIndex: target.editableIndex
    });
  }
}

function onRemove(payload) {
  const target = payload && payload.target;
  if (!target) return;
  if (target.targetKind === 'decorative-divider') {
    removeDecorativeForTarget(target);
    return;
  }
  if (target.targetKind === 'decorative-background') {
    removeBackgroundImageForTarget(target);
    return;
  }
  reportError(
    'decorative-action',
    new Error(`Unknown target kind: ${target.targetKind || 'missing'}`),
    { user: 'toast' }
  );
}

function getTriggerEl(payload) {
  const triggerEl = payload && payload.triggerEl;
  if (typeof HTMLElement !== 'undefined') {
    return triggerEl instanceof HTMLElement ? triggerEl : null;
  }
  return triggerEl && typeof triggerEl.focus === 'function' ? triggerEl : null;
}
